import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { fetchDecalBills } from "../lib/api";
import type { DecalBillSummary, BillFilters, RiskLevel, DecalDomain } from "../lib/types";
import DomainPill from "../components/ui/DomainPill";
import { cn } from "../lib/cn";

// ─── Constants ────────────────────────────────────────────────────────────────

const DOMAINS: { value: DecalDomain; label: string }[] = [
  { value: "LICENSING_COMPLIANCE",        label: "Licensing & Compliance" },
  { value: "HEALTH_SAFETY",               label: "Health & Safety" },
  { value: "PREK_PROGRAMS",               label: "Pre-K Programs" },
  { value: "WORKFORCE",                   label: "Workforce" },
  { value: "QUALITY_INITIATIVES",         label: "Quality Ratings" },
  { value: "FAMILY_ACCESS_AFFORDABILITY", label: "Family Access" },
  { value: "DATA_PRIVACY_REPORTING",      label: "Data & Privacy" },
  { value: "TECHNOLOGY_AI_EDUCATION",     label: "Tech & AI" },
  { value: "LOTTERY_HOPE_FUNDING",        label: "Lottery / HOPE" },
  { value: "CAMPUS_ADJACENT",             label: "Campus Facilities" },
];

const RISK_LEVELS: { value: RiskLevel; color: string; bg: string }[] = [
  { value: "High",   color: "text-risk-high",   bg: "bg-red-50 border-risk-high-ring text-risk-high" },
  { value: "Medium", color: "text-risk-medium",  bg: "bg-amber-50 border-risk-medium-ring text-risk-medium" },
  { value: "Low",    color: "text-risk-low",     bg: "bg-teal-50 border-risk-low-ring text-risk-low" },
];

const SCORE_COLOR: Record<string, string> = {
  High:   "text-risk-high",
  Medium: "text-risk-medium",
  Low:    "text-risk-low",
};
const SCORE_BG: Record<string, string> = {
  High:   "bg-red-50",
  Medium: "bg-amber-50",
  Low:    "bg-teal-50",
};

// ─── Search bar ───────────────────────────────────────────────────────────────

function SearchBar({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="relative">
      <svg className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-tertiary"
        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1116.65 16.65z" />
      </svg>
      <input
        type="search"
        aria-label="Search bills by title or number"
        placeholder="Search bills by title or bill number…"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          "w-full rounded-2xl border border-border bg-surface-raised py-3 pl-11 pr-4",
          "text-sm text-ink placeholder:text-ink-tertiary shadow-sm",
          "focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary-light",
          "transition-shadow duration-150"
        )}
      />
    </div>
  );
}

// ─── Filter sidebar ───────────────────────────────────────────────────────────

function FilterSidebar({ filters, onChange }: { filters: BillFilters; onChange: (f: BillFilters) => void }) {
  function toggleDomain(d: DecalDomain) {
    onChange({ ...filters, domain: filters.domain === d ? undefined : d, page: 1 });
  }
  function toggleRisk(r: RiskLevel) {
    onChange({ ...filters, risk_level: filters.risk_level === r ? undefined : r, page: 1 });
  }
  function clearAll() {
    onChange({ search: filters.search, page: 1, limit: filters.limit });
  }

  const hasFilters = filters.domain || filters.risk_level;

  return (
    <aside className="w-52 shrink-0 space-y-6" aria-label="Bill filters">
      <div className="flex items-center justify-between">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-ink-tertiary">Filters</h2>
        {hasFilters && (
          <button onClick={clearAll}
            className="flex items-center gap-1 text-xs text-ink-tertiary transition-colors hover:text-ink focus:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-primary">
            <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            Clear all
          </button>
        )}
      </div>

      {/* Domain */}
      <div className="space-y-1">
        <p className="mb-2 text-xs font-medium text-ink-secondary">Domain</p>
        {DOMAINS.map(({ value, label }) => (
          <button key={value} onClick={() => toggleDomain(value)} aria-pressed={filters.domain === value}
            className={cn(
              "flex w-full items-center rounded-lg px-2.5 py-2 text-sm transition-colors duration-150",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              filters.domain === value
                ? "bg-primary-light font-medium text-primary"
                : "text-ink-secondary hover:bg-surface-sunken hover:text-ink"
            )}>
            {label}
          </button>
        ))}
      </div>

      {/* Risk level */}
      <div className="space-y-1.5">
        <p className="mb-2 text-xs font-medium text-ink-secondary">Risk level</p>
        {RISK_LEVELS.map(({ value, bg }) => (
          <button key={value} onClick={() => toggleRisk(value)} aria-pressed={filters.risk_level === value}
            aria-label={`Filter by ${value} risk`}
            className={cn(
              "flex w-full items-center rounded-lg border px-3 py-1.5 text-xs font-semibold transition-all duration-150",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary",
              filters.risk_level === value
                ? cn(bg, "shadow-sm")
                : "border-border-subtle bg-surface text-ink-secondary hover:border-border"
            )}>
            {value} Risk
          </button>
        ))}
      </div>
    </aside>
  );
}

// ─── Bill row ─────────────────────────────────────────────────────────────────

function BillRow({ bill }: { bill: DecalBillSummary }) {
  const navigate = useNavigate();

  return (
    <article
      onClick={() => navigate(`/bills/${bill.bill_id}`)}
      className={cn(
        "group flex cursor-pointer items-start gap-4 rounded-2xl border border-border-subtle bg-surface-raised p-5",
        "shadow-sm transition-all duration-200 hover:-translate-y-px hover:border-border hover:shadow-md"
      )}
    >
      {/* Score pill */}
      <div className={cn(
        "flex w-14 shrink-0 flex-col items-center rounded-xl py-2.5",
        SCORE_BG[bill.risk_level] ?? "bg-surface-sunken"
      )}>
        <span className={cn("text-xl font-bold tabular-nums leading-none", SCORE_COLOR[bill.risk_level] ?? "text-ink")}>
          {bill.impact_score}
        </span>
        <span className="mt-0.5 text-2xs font-medium text-ink-tertiary">score</span>
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span className="rounded-md bg-surface-sunken px-2 py-0.5 font-mono text-xs font-medium text-ink-secondary">
            {bill.state} · {bill.bill_number}
          </span>
          <span className="text-xs text-ink-tertiary">{bill.status_label}</span>
        </div>
        <h3 className="mb-1 line-clamp-2 text-sm font-semibold leading-snug text-ink transition-colors duration-150 group-hover:text-primary">
          {bill.title}
        </h3>
        {bill.last_action && (
          <p className="mb-2 text-xs text-ink-tertiary">{bill.last_action}</p>
        )}
        {bill.decal_domains.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {bill.decal_domains.slice(0, 3).map((d) => (
              <DomainPill key={d} domain={d as DecalDomain} size="sm" />
            ))}
          </div>
        )}
      </div>

      {/* Arrow */}
      <svg className="mt-1 h-4 w-4 shrink-0 text-ink-disabled opacity-0 transition-all duration-150 group-hover:translate-x-0.5 group-hover:text-primary group-hover:opacity-100"
        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
      </svg>
    </article>
  );
}

// ─── Pagination ───────────────────────────────────────────────────────────────

function Pagination({ page, total, limit, onPageChange }: {
  page: number; total: number; limit: number; onPageChange: (p: number) => void;
}) {
  const totalPages = Math.ceil(total / limit);
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-3 pt-6">
      <button onClick={() => onPageChange(page - 1)} disabled={page === 1}
        className="rounded-full border border-border px-4 py-1.5 text-sm text-ink-secondary transition-colors hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-40">
        Previous
      </button>
      <span className="text-xs text-ink-tertiary">{page} of {totalPages}</span>
      <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}
        className="rounded-full border border-border px-4 py-1.5 text-sm text-ink-secondary transition-colors hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-40">
        Next
      </button>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function BillExplorer() {
  const [filters, setFilters] = useState<BillFilters>({ page: 1, limit: 25 });
  const [searchInput, setSearchInput] = useState("");
  const [bills, setBills] = useState<DecalBillSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((prev) => ({ ...prev, search: searchInput || undefined, page: 1 }));
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const load = useCallback(async (f: BillFilters) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchDecalBills(f);
      setBills(res.data);
      setTotal(res.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load bills");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(filters); }, [filters, load]);

  return (
    <div className="mx-auto max-w-7xl animate-fade-in px-6 py-8">

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-ink">Bill Explorer</h1>
        <p className="mt-1 text-sm text-ink-secondary">
          All DECAL-relevant bills tracked in the current Georgia legislative session
        </p>
      </div>

      {/* Search */}
      <div className="mb-8">
        <SearchBar value={searchInput} onChange={setSearchInput} />
      </div>

      {/* Layout */}
      <div className="flex gap-10">
        <FilterSidebar filters={filters} onChange={setFilters} />

        <div className="min-w-0 flex-1">
          {!loading && !error && (
            <p className="mb-4 text-xs text-ink-tertiary">
              {total.toLocaleString()} {total === 1 ? "bill" : "bills"}
              {filters.domain || filters.risk_level ? " matching your filters" : " tracked"}
            </p>
          )}

          {error && (
            <div className="rounded-2xl border border-risk-high-ring bg-risk-high-bg px-5 py-4 text-sm text-risk-high">
              {error}
            </div>
          )}

          {loading && (
            <div className="space-y-3">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="h-28 animate-pulse rounded-2xl bg-surface-sunken" />
              ))}
            </div>
          )}

          {!loading && !error && bills.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-surface-sunken">
                <svg className="h-6 w-6 text-ink-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 1116.65 16.65z" />
                </svg>
              </div>
              <p className="text-sm font-semibold text-ink-secondary">No bills match your search</p>
              <p className="mt-1 text-xs text-ink-tertiary">Try a different keyword or clear your filters</p>
            </div>
          )}

          {!loading && !error && bills.length > 0 && (
            <div className="space-y-3">
              {bills.map((b) => <BillRow key={b.bill_id} bill={b} />)}
            </div>
          )}

          <Pagination
            page={filters.page ?? 1}
            total={total}
            limit={filters.limit ?? 25}
            onPageChange={(p) => setFilters((prev) => ({ ...prev, page: p }))}
          />
        </div>
      </div>
    </div>
  );
}
